import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ClientFilters = ({ 
  onSearch, 
  onFilterChange, 
  onClearFilters,
  totalClients,
  filteredCount 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    ageRange: '',
    lastTraining: '',
    activePlans: ''
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const ageRangeOptions = [
    { value: '', label: 'All Ages' },
    { value: '18-30', label: '18-30 years' },
    { value: '31-50', label: '31-50 years' },
    { value: '51-65', label: '51-65 years' },
    { value: '66-75', label: '66-75 years' },
    { value: '76+', label: '76+ years' }
  ];

  const lastTrainingOptions = [
    { value: '', label: 'Any Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'never', label: 'Never Trained' }
  ];

  const activePlansOptions = [
    { value: '', label: 'Any Plans' },
    { value: '0', label: 'No Active Plans' },
    { value: '1', label: '1 Active Plan' },
    { value: '2+', label: '2+ Active Plans' }
  ];

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    onSearch(value);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearAll = () => {
    setSearchTerm('');
    setFilters({
      status: '',
      ageRange: '',
      lastTraining: '',
      activePlans: ''
    });
    setShowAdvanced(false);
    onSearch('');
    onFilterChange({
      status: '',
      ageRange: '',
      lastTraining: '',
      activePlans: ''
    });
    onClearFilters();
  };

  const hasActiveFilters = searchTerm || Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
      {/* Search and Quick Actions */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
          />
          <Input
            type="search"
            placeholder="Search clients by name, RFID ID, or identifier..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e?.target?.value)}
            className="pl-10"
          />
        </div>
        
        <Button
          variant="outline"
          onClick={() => setShowAdvanced(!showAdvanced)}
          iconName={showAdvanced ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
          iconSize={16}
        >
          Advanced Filters
        </Button>
        
        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={handleClearAll}
            iconName="X"
            iconPosition="left"
            iconSize={16}
          >
            Clear All
          </Button>
        )}
      </div>
      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border">
          <Select
            label="Status"
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            placeholder="Filter by status"
          />
          
          <Select
            label="Age Range"
            options={ageRangeOptions}
            value={filters?.ageRange}
            onChange={(value) => handleFilterChange('ageRange', value)}
            placeholder="Filter by age"
          />
          
          <Select
            label="Last Training"
            options={lastTrainingOptions}
            value={filters?.lastTraining}
            onChange={(value) => handleFilterChange('lastTraining', value)}
            placeholder="Filter by training"
          />
          
          <Select
            label="Active Plans"
            options={activePlansOptions}
            value={filters?.activePlans}
            onChange={(value) => handleFilterChange('activePlans', value)}
            placeholder="Filter by plans"
          />
        </div>
      )}
      {/* Results Summary */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <span>
            Showing {filteredCount} of {totalClients} clients
          </span>
          
          {hasActiveFilters && (
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={16} />
              <span>Filters active</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            iconSize={16}
          >
            Export
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Upload"
            iconPosition="left"
            iconSize={16}
          >
            Import
          </Button>
        </div>
      </div>
      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2">
          {searchTerm && (
            <div className="flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
              <Icon name="Search" size={14} />
              <span>Search: "{searchTerm}"</span>
              <button
                onClick={() => handleSearchChange('')}
                className="hover:bg-primary/20 rounded-full p-1"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
          
          {Object.entries(filters)?.map(([key, value]) => {
            if (!value) return null;
            
            const getFilterLabel = (key, value) => {
              const option = {
                status: statusOptions,
                ageRange: ageRangeOptions,
                lastTraining: lastTrainingOptions,
                activePlans: activePlansOptions
              }?.[key]?.find(opt => opt?.value === value);
              
              return option ? option?.label : value;
            };
            
            return (
              <div
                key={key}
                className="flex items-center space-x-2 bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm"
              >
                <span>{getFilterLabel(key, value)}</span>
                <button
                  onClick={() => handleFilterChange(key, '')}
                  className="hover:bg-secondary/20 rounded-full p-1"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientFilters;